import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Spinner } from 'react-bootstrap';
import { 
  FaUsers, 
  FaUserCheck, 
  FaUserClock, 
  FaBook, 
  FaLayerGroup, 
  FaFileAlt, 
  FaQuestionCircle,
  FaRupeeSign,
  FaCheckCircle,
  FaClock,
  FaEnvelope,
  FaChartLine,
  FaGraduationCap,
  FaClipboardList
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import Header from '../../components/common/Header';
import DashboardService from '../../services/dashboardService';
import '../../styles/admin-dashboard.css';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const response = await DashboardService.getStats();
      console.log('Dashboard data:', response);
      
      if (response.status && response.data) {
        setStats(response.data);
      } else {
        console.error('Invalid response structure:', response);
      }
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div>
        <Header />
        <div className="dashboard-loading">
          <Spinner animation="border" variant="primary" />
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Extract data with fallbacks
  const totalStudents = stats?.students?.total_students || 0;
  const pendingStudents = stats?.students?.pending_students || 0;
  const totalCourses = stats?.courses || 0;
  const totalBatches = stats?.batches || 0;
  const totalTests = stats?.tests || 0;
  const totalAssignments = stats?.assignments || 0;
  const totalRevenue = stats?.revenue?.total || 0;
  const totalTransactions = stats?.revenue?.transactions || 0;
  const pendingVerifications = stats?.pending_actions?.student_verifications || 0;
  const pendingGrading = stats?.pending_actions?.assignment_grading || 0;
  const pendingInquiries = stats?.pending_actions?.inquiries || 0;

  return (
    <div>
      <Header />
      <div className="admin-dashboard">
        <Container fluid className="p-4">
          {/* Welcome Header */}
          <div className="dashboard-header mb-4">
            <div className="welcome-section">
              <h1 className="dashboard-title">
                <FaChartLine className="me-2 text-primary" />
                Admin Dashboard
              </h1>
              <p className="text-muted">Welcome back! Here's what's happening with your institute today.</p>
            </div>
            <div className="date-section">
              <span className="badge bg-light text-dark p-2">
                {new Date().toLocaleDateString('en-IN', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </span>
            </div>
          </div>

          {/* First Row - Core Stats */}
          <Row className="g-4 mb-4">
            <Col lg={3} md={6}>
              <Card className="stat-card stat-card-primary">
                <Card.Body>
                  <div className="stat-icon">
                    <FaGraduationCap />
                  </div>
                  <div className="stat-info">
                    <h3 className="stat-value">{totalStudents}</h3>
                    <p className="stat-label">Total Students</p>
                    <small className="stat-trend">
                      {pendingStudents > 0 && `${pendingStudents} pending approval`}
                    </small>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={3} md={6}>
              <Card className="stat-card stat-card-success">
                <Card.Body>
                  <div className="stat-icon">
                    <FaBook />
                  </div>
                  <div className="stat-info">
                    <h3 className="stat-value">{totalCourses}</h3>
                    <p className="stat-label">Active Courses</p>
                    <small className="stat-trend">
                      <FaLayerGroup className="me-1" /> {totalBatches} Batches
                    </small>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={3} md={6}>
              <Card className="stat-card stat-card-warning">
                <Card.Body>
                  <div className="stat-icon">
                    <FaQuestionCircle />
                  </div>
                  <div className="stat-info">
                    <h3 className="stat-value">{totalTests}</h3>
                    <p className="stat-label">Published Tests</p>
                    <small className="stat-trend">
                      <FaClipboardList className="me-1" /> {totalAssignments} Assignments
                    </small>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={3} md={6}>
              <Card className="stat-card stat-card-info">
                <Card.Body>
                  <div className="stat-icon">
                    <FaRupeeSign />
                  </div>
                  <div className="stat-info">
                    <h3 className="stat-value">{formatCurrency(totalRevenue)}</h3>
                    <p className="stat-label">Last 30 Days Revenue</p>
                    <small className="stat-trend">
                      {totalTransactions} transactions
                    </small>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Second Row - Pending Actions */}
          <Row className="g-4 mb-4">
            <Col lg={4} md={6}>
              <Card className="stat-card stat-card-orange">
                <Card.Body>
                  <div className="stat-icon">
                    <FaUserClock />
                  </div>
                  <div className="stat-info">
                    <h3 className="stat-value">{pendingVerifications}</h3>
                    <p className="stat-label">Student Verifications Pending</p>
                    <small className="stat-trend">Awaiting admin approval</small>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={4} md={6}>
              <Card className="stat-card stat-card-purple">
                <Card.Body>
                  <div className="stat-icon">
                    <FaFileAlt />
                  </div>
                  <div className="stat-info">
                    <h3 className="stat-value">{pendingGrading}</h3>
                    <p className="stat-label">Assignments to Grade</p>
                    <small className="stat-trend">Submissions pending review</small>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={4} md={12}>
              <Card className="stat-card stat-card-danger">
                <Card.Body>
                  <div className="stat-icon">
                    <FaEnvelope />
                  </div>
                  <div className="stat-info">
                    <h3 className="stat-value">{pendingInquiries}</h3>
                    <p className="stat-label">Unread Inquiries</p>
                    <small className="stat-trend">New messages to reply</small>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Third Row - Recent Activity & Monthly Revenue */}
          <Row className="g-4">
            <Col lg={7}>
              <Card className="shadow-sm">
                <Card.Header className="bg-white border-0 pt-3">
                  <h5 className="mb-0">
                    <FaClock className="me-2 text-primary" />
                    Recent Activity
                  </h5>
                </Card.Header>
                <Card.Body className="p-0">
                  {stats?.recent_activities?.length === 0 ? (
                    <div className="text-center text-muted py-4">
                      No recent activities
                    </div>
                  ) : (
                    <div className="activity-list">
                      {stats?.recent_activities?.slice(0, 8).map((activity, idx) => (
                        <div key={idx} className="activity-item">
                          <div className={`activity-icon bg-${getActivityColor(activity.type)}`}>
                            {getActivityIconComponent(activity.type)}
                          </div>
                          <div className="activity-content">
                            <div className="activity-title">{activity.title}</div>
                            <div className="activity-description">{activity.description}</div>
                            <div className="activity-time">{formatDate(activity.date)}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>

            <Col lg={5}>
              <Card className="shadow-sm">
                <Card.Header className="bg-white border-0 pt-3">
                  <h5 className="mb-0">
                    <FaChartLine className="me-2 text-success" />
                    Monthly Revenue
                  </h5>
                </Card.Header>
                <Card.Body>
                  {stats?.monthly_revenue && stats.monthly_revenue.length > 0 ? (
                    <div className="revenue-list">
                      {stats.monthly_revenue.map((item, idx) => {
                        const date = new Date(item.month + '-01');
                        const monthName = date.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
                        return (
                          <div key={idx} className="revenue-item">
                            <div className="revenue-month">{monthName}</div>
                            <div className="revenue-amount">{formatCurrency(item.revenue)}</div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center text-muted py-4">
                      No revenue data available
                    </div>
                  )}
                </Card.Body>
              </Card>

              {/* Quick Actions */}
              <Card className="shadow-sm mt-4">
                <Card.Header className="bg-white border-0 pt-3">
                  <h5 className="mb-0">
                    <FaCheckCircle className="me-2 text-primary" />
                    Quick Actions
                  </h5>
                </Card.Header>
                <Card.Body>
                  <div className="quick-actions-grid">
                    <button className="quick-action-btn" onClick={() => window.location.href = '/admin/students/manage'}>
                      <FaUsers /> Manage Students
                    </button>
                    <button className="quick-action-btn" onClick={() => window.location.href = '/admin/courses'}>
                      <FaBook /> Add Course
                    </button>
                    <button className="quick-action-btn" onClick={() => window.location.href = '/admin/batches'}>
                      <FaLayerGroup /> Create Batch
                    </button>
                    <button className="quick-action-btn" onClick={() => window.location.href = '/admin/tests'}>
                      <FaQuestionCircle /> Create Test
                    </button>
                    <button className="quick-action-btn" onClick={() => window.location.href = '/admin/attendance/daily'}>
                      <FaCheckCircle /> Mark Attendance
                    </button>
                    <button className="quick-action-btn" onClick={() => window.location.href = '/admin/inquiries'}>
                      <FaEnvelope /> View Inquiries
                    </button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </div>
  );
};

// Helper functions for activity icons
const getActivityColor = (type) => {
  switch(type) {
    case 'student': return 'success';
    case 'test': return 'info';
    case 'payment': return 'warning';
    default: return 'primary';
  }
};

const getActivityIconComponent = (type) => {
  switch(type) {
    case 'student': return <FaUserCheck />;
    case 'test': return <FaCheckCircle />;
    case 'payment': return <FaRupeeSign />;
    default: return <FaChartLine />;
  }
};

export default Dashboard;